import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  Button,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ExternalProjectImportError,
  ExternalProjectImportPreview,
} from '@provision/entity-types';
import { portalPaths } from '@provision/portal-routes';
import {
  projectImportToString,
  totalImportCount,
} from 'src/components/ImportDtoolsProjectModal/import-results.utils';
import { Modals, useModalMethods } from 'src/store/modals';
import { useNavigateRoute } from 'src/utils/routing';

export type ImportResultsProps = {
  projectIds: number[];
  records: ExternalProjectImportPreview[];
  loading: boolean;
  errors?: ExternalProjectImportError<string | number>[];
};

export const ImportResults = ({
  records,
  loading,
  projectIds,
  errors,
}: ImportResultsProps) => {
  const theme = useTheme();
  const totals = totalImportCount(records);
  const { navigate } = useNavigateRoute();
  const { closeModal } = useModalMethods(Modals.ImportPortalioProjects);

  if (loading) {
    return (
      <Stack spacing={2}>
        <Skeleton variant="rounded" height={theme.spacing(9)} />
        <Stack spacing={2}>
          {[0, 1].map((key) => (
            <Skeleton key={key} variant="rounded" height={theme.spacing(11)} />
          ))}
        </Stack>
        <Skeleton variant="rounded" height={theme.spacing(22)} />
      </Stack>
    );
  }

  const totalProjectsCount = projectIds.length;
  const failedProjectsCount = errors?.length ?? 0;
  const isPartiallyFailed = failedProjectsCount > 0 && failedProjectsCount < totalProjectsCount;
  const isAllProjectsFailed = totalProjectsCount === failedProjectsCount && totalProjectsCount > 0;

  let statusColor = theme.palette.success;
  let StatusIcon = CheckCircleOutlineIcon;

  if (isPartiallyFailed) {
    statusColor = theme.palette.warning;
    StatusIcon = WarningAmberOutlinedIcon;
  }

  if (isAllProjectsFailed) {
    statusColor = theme.palette.error;
    StatusIcon = ErrorOutlineOutlinedIcon;
  }

  return (
    <Stack spacing={2}>
      <Paper
        variant="outlined"
        sx={{
          backgroundColor: statusColor.light,
          borderColor: statusColor.light,
          py: 2,
          px: 3,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center">
          <StatusIcon sx={{ color: statusColor.dark }} />
          <Typography variant="subtitle1">
            {loading ? 'Importing...' : `${records.length} of ${projectIds.length} imported`}
          </Typography>
        </Stack>
      </Paper>

      <Stack spacing={2}>
        {records.map((record) => (
          <Paper
            key={record.id}
            variant="outlined"
            sx={{
              borderRadius: 1,
              px: 3,
              py: 2,
            }}
          >
            <Stack direction="row" justifyContent="space-between" spacing={2}>
              <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <CheckCircleOutlineIcon color="success" fontSize="small" />
                  <Typography variant="subtitle1">{record.projectName}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {projectImportToString(record)}
                </Typography>
              </Stack>
              {record.addressId ? (
                <Button
                  onClick={() => {
                    closeModal();
                    navigate(portalPaths.site.full(record.addressId!));
                  }}
                  variant="text"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ alignSelf: 'center' }}
                >
                  View
                </Button>
              ) : null}
            </Stack>
          </Paper>
        ))}
      </Stack>

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 1,
        }}
      >
        <Stack
          sx={{
            px: 3,
            py: 1,
          }}
        >
          <Typography variant="overline" color="text.secondary">
            Total
          </Typography>
        </Stack>
        <Divider variant="fullWidth" />
        <Stack
          spacing={1}
          sx={{
            px: 3,
            py: 1,
          }}
        >
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Sites:
            </Typography>
            <Typography variant="body2">{totals.sites}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Clients:
            </Typography>
            <Typography variant="body2">{totals.clients}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">
              Projects:
            </Typography>
            <Typography variant="body2">{totals.projects}</Typography>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};
